import React from 'react'
import Question, { ResetQuestion } from './Question'
import { QuizProps, AppConfigPropsType, QuestionType, ContactFieldGroup } from '../types'
import styles from './Quiz.module.css'
import QuizButton from './QuizButton'
import ProgressBar from './ProgressBar'
import { isEmpty } from '../utils'
import { Fade } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2/Grid2'

type QuizState = {
  config: AppConfigPropsType
  question_inputs: QuestionType[]
  contact_info_groups: ContactFieldGroup[]
  current_page_index: number
  completed_pages: boolean[]
  showResults: boolean
  updating: boolean
}

class Quiz extends React.Component<QuizProps, QuizState> {
  constructor(props: QuizProps) {
    super(props)
    this.state = {
      config: props.config,
      question_inputs: props.config.Questions,
      contact_info_groups: props.config.ContactFieldGroups,
      current_page_index: 0,
      completed_pages: Array(props.config.Questions.length).fill(false),
      showResults: false,
      updating: false,
    }
  }

  componentDidMount(): void {
    const completed_pages = this.state.completed_pages
    completed_pages[this.state.current_page_index] = this.ValidateQuestion()
    this.setState({
      completed_pages: completed_pages,
    })
  }

  Reset = () => {
    this.setState({
      question_inputs: this.state.config.Questions.map(ResetQuestion),
      current_page_index: 0,
      completed_pages: Array(this.state.config.Questions.length).fill(false),
      showResults: false,
    })
  }

  ResetPage = () => {
    this.Reset()
  }

  IncrementPage = () => {
    if (!this.props.config) return
    let { current_page_index } = this.state
    const completed_pages = this.state.completed_pages
    completed_pages[current_page_index] = true
    current_page_index = Math.min(this.props.config.Questions.length - 1, current_page_index + 1)
    completed_pages[current_page_index] = this.ValidateQuestion(current_page_index)
    this.setState({
      current_page_index,
      showResults: current_page_index === this.props.config.Questions.length,
      completed_pages: completed_pages,
      updating: false,
    })
  }

  DecrementPage = () => {
    let { current_page_index } = this.state
    current_page_index = Math.max(0, current_page_index - 1)
    this.setState({
      current_page_index
    })
  }

  UpdateAnswers = (question_id: string, answer: string) => {
    this.setState({
      updating: true,
    })
    for (let qi of this.state.question_inputs) {
      if (qi.question_id === question_id) {
        qi.input = answer
        break
      }
      if (qi.follow_up_questions) {
        const follow_up_question = qi.follow_up_questions.find(fq => fq.question_id === question_id)
        if (follow_up_question) {
          follow_up_question.input = answer
          break
        }
      }
    }
    this.setState({
      question_inputs: this.state.question_inputs,
    })
    const currentQuestionValid = this.ValidateQuestion()
    setTimeout(() => {
      if (
        this.state.config.AutoNext &&
        !this.state.completed_pages[this.state.current_page_index] &&
        this.state.current_page_index <= this.state.config.Questions.length - 1 &&
        currentQuestionValid
      ) {
        this.IncrementPage()
      } else {
        this.setState({
          updating: false,
        })
      }
      if (currentQuestionValid) {
        const completed_pages = this.state.completed_pages
        completed_pages[this.state.current_page_index] = true
        this.setState({
          completed_pages,
        })
      }
    }, 300)
  }

  ValidateQuestion = (index?: number): boolean => {
    const page_index = index || this.state.current_page_index
    const current_question = this.state.question_inputs[page_index]
    if (current_question) {
      if (!isEmpty(current_question.input)) {
        if (current_question.follow_up_questions) {
          return current_question.follow_up_questions.every(fq => current_question.input && fq.conditional_answers.includes(current_question.input) ? !isEmpty(fq.input) : true)
        }
        return true
      }
      return false
    }
    return true
  }

  UpdateContactField = (group_id: string, name: string, value: string) => {
    found:
    for (let group of this.state.contact_info_groups) {
      if (group.group_id === group_id) {
        for (let field of group.fields) {
          if (field.name === name) {
            field.value = value
            break found
          }
        }
      }
    }
    this.setState({
      contact_info_groups: this.state.contact_info_groups,
    })
  }

  IsCurrentQuestionCompleted = (): boolean => {
    return this.state.completed_pages[this.state.current_page_index]
  }

  render = () => {
    if (!this.props.config) {
      return null
    }
    const {
      current_page_index,
      question_inputs,
      completed_pages,
    } = this.state
    const overrideStyles: React.CSSProperties = {

    }
    return (
      <div className={styles.Quiz} style={{ ...overrideStyles }}>
        <div>
          <ProgressBar value={completed_pages.reduce((sum, completed) => completed ? sum + 1 : sum, 0) / completed_pages.length * 100} />
        </div>
        <div className={[styles.QuizBody, !this.props.config.DisplayAsModal ? styles.QuizBodyStatic : ''].join(' ')}>
          <div>
            {question_inputs && question_inputs.map((q, index) => {
              const isActive = current_page_index === index
              const current_input = question_inputs.find(qi => qi.question_id === q.question_id)
              return (
                <div key={`${q.question_id}`} className={isActive ? styles.QuizQuestionShown : styles.QuizQuestionHidden}>
                  <Fade
                    in
                    timeout={300}
                  >
                    <div>
                      <Question
                        config={{ ...q }}
                        Index={index}
                        IsActive={isActive}
                        Answer={current_input?.input}
                        OnUpdate={this.UpdateAnswers}
                        disabled={this.state.updating}
                      />
                    </div>
                  </Fade>
                  {q.follow_up_questions && q.follow_up_questions.length > 0 && q.follow_up_questions.map(fq => {
                    if (current_input && current_input.input && fq.conditional_answers.includes(current_input.input)) {
                      return (
                        <Fade
                          in
                          timeout={300}
                          key={fq.question_id}
                        >
                          <div>
                            <Question
                              config={{ ...fq }}
                              Index={index}
                              IsActive={isActive}
                              Answer={fq.input}
                              OnUpdate={this.UpdateAnswers}
                              disabled={this.state.updating}
                            />
                          </div>
                        </Fade>
                      )
                    }
                    return null
                  })}
                </div>
              )
            })}
          </div>
        </div>
        <div className={styles.QuizControls}>
          <Grid container justifyContent={'space-between'}>
            <Grid>
              {current_page_index > 0 &&
                <QuizButton
                  Action={() => this.DecrementPage()}
                  label='Previous'
                  variant='outlined'
                  disabled={current_page_index === 0}
                />
              }
            </Grid>
            <Grid>
              {current_page_index < this.props.config.Questions.length - 1 &&
                <QuizButton
                  Action={() => this.IncrementPage()}
                  label='Next'
                  variant='contained'
                  disabled={!this.IsCurrentQuestionCompleted()}
                />
              }
              {current_page_index === this.props.config.Questions.length - 1 &&
                <QuizButton
                  Action={() => this.props.OnComplete()}
                  label='Complete'
                  variant='contained'
                  disabled={!this.IsCurrentQuestionCompleted()}
                />
              }
            </Grid>
          </Grid>
        </div>
        <div style={{ textAlign: 'center', fontSize: 'small' }}>
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy">Privacy Policy</a> and{' '}
          <a href="https://policies.google.com/terms">Terms of Service</a> apply.
        </div>
      </div>
    )
  }
}

export default Quiz
